"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownList,
    Icon,
    Table,
} from "@farm-junction/design-library";
import {
    EMI_CALCULATOR_METRICS_EVENT,
    EMI_SELECTED_VARIANT_EVENT,
    type EmiCalculatorMetricsDetail,
} from "@/component/ui/CityPriceGrid/CityPriceGridReactive";
import { getLocationCookie } from "@/app/utilities/locationCookie";
import LeadButton from "@/component/ui/Lead/LeadButton";
import {
    calculateEMI,
    EMI_DEFAULT_INTEREST_RATE,
    formatEmiCurrency,
    getDefaultDownPayment,
    getEmiScheduleTenures,
    resolveDefaultEmiVariant,
} from "@/lib/utils/emi";
import { getOnRoadPrice } from "@/utils/onRoadPrice";
import type { CityData } from "@/types/city";

const BRAND_COLOR = "#008C8B";

type EmiScheduleWidgetProps = {
    brandName: string;
    modelName: string;
    variants: any[];
    initialSelectedVariantId?: string;
    defaultLocation?: CityData | null;
    locationInfo?: any;
    carId: string;
    interestRate?: number;
    isDiscontinued?: boolean;
    isUpcoming?: boolean;
    className?: string;
};

export default function EmiScheduleWidget({
    brandName,
    modelName,
    variants,
    initialSelectedVariantId,
    defaultLocation = null,
    locationInfo,
    carId,
    interestRate = EMI_DEFAULT_INTEREST_RATE,
    isDiscontinued = false,
    isUpcoming = false,
    className = "",
}: EmiScheduleWidgetProps) {
    const hiddenLeadBtnRef = useRef<HTMLDivElement>(null);
    const [isVariantDropdownOpen, setIsVariantDropdownOpen] = useState(false);
    const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(() => {
        return resolveDefaultEmiVariant(variants, initialSelectedVariantId)?._id;
    });
    const [pricingLocation, setPricingLocation] = useState<CityData | null>(
        () => (locationInfo ?? defaultLocation) as CityData | null,
    );
    const [calculatorMetrics, setCalculatorMetrics] = useState<EmiCalculatorMetricsDetail | null>(null);

    const selectableVariants = useMemo(
        () => variants.filter((item) => item?.is_sanity_done !== false && item?.status !== "discontinued"),
        [variants],
    );

    const selectedVariant = useMemo(
        () => resolveDefaultEmiVariant(selectableVariants, selectedVariantId),
        [selectableVariants, selectedVariantId],
    );

    useEffect(() => {
        const resolved = resolveDefaultEmiVariant(variants, initialSelectedVariantId);
        if (resolved?._id) setSelectedVariantId(resolved._id);
    }, [initialSelectedVariantId, variants]);

    useEffect(() => {
        let mounted = true;

        const resolveCity = async () => {
            const latestLocation = getLocationCookie();
            let cityDataFull: CityData | null = null;

            if (latestLocation?.slug) {
                try {
                    const { getCityBySlug } = await import("@/app/utilities/apis");
                    cityDataFull = (await getCityBySlug(latestLocation.slug)) as CityData;
                } catch {
                    cityDataFull = null;
                }
            }

            if (!cityDataFull && (!latestLocation || Object.keys(latestLocation).length === 0)) {
                cityDataFull =
                    defaultLocation && Object.keys(defaultLocation).length > 0 ? defaultLocation : null;
            }

            if (!mounted) return;
            setPricingLocation(cityDataFull ?? (locationInfo as CityData) ?? defaultLocation);
        };

        resolveCity();
        window.addEventListener("location-updated", resolveCity);
        return () => {
            mounted = false;
            window.removeEventListener("location-updated", resolveCity);
        };
    }, [defaultLocation, locationInfo]);

    useEffect(() => {
        const onVariantChange = (event: Event) => {
            const detail = (event as CustomEvent).detail;
            if (detail?._id) setSelectedVariantId(detail._id);
        };
        window.addEventListener(EMI_SELECTED_VARIANT_EVENT, onVariantChange);
        return () => window.removeEventListener(EMI_SELECTED_VARIANT_EVENT, onVariantChange);
    }, []);

    useEffect(() => {
        const onMetricsChange = (event: Event) => {
            const detail = (event as CustomEvent<EmiCalculatorMetricsDetail>).detail;
            if (detail) setCalculatorMetrics(detail);
        };
        window.addEventListener(EMI_CALCULATOR_METRICS_EVENT, onMetricsChange);
        return () => window.removeEventListener(EMI_CALCULATOR_METRICS_EVENT, onMetricsChange);
    }, []);

    const pricing = useMemo(() => {
        if (!selectedVariant) {
            return { onRoadPrice: 0, loanAmount: 0, downPayment: 0 };
        }
        const onRoadPrice =
            getOnRoadPrice(selectedVariant, pricingLocation)?.onRoadPrice ||
            Number(selectedVariant?.price) ||
            0;
        const downPayment = getDefaultDownPayment(onRoadPrice);
        const loanAmount = Math.max(0, onRoadPrice - downPayment);
        return { onRoadPrice, loanAmount, downPayment };
    }, [pricingLocation, selectedVariant]);

    const activePricing = useMemo(() => {
        const onRoadPrice = calculatorMetrics?.effectivePrice ?? pricing.onRoadPrice;
        const downPayment = calculatorMetrics?.downPayment ?? pricing.downPayment;
        const loanAmount = calculatorMetrics?.loanAmount ?? pricing.loanAmount;
        const activeInterestRate = calculatorMetrics?.interestRate ?? interestRate;
        return { onRoadPrice, downPayment, loanAmount, activeInterestRate };
    }, [calculatorMetrics, interestRate, pricing]);

    const scheduleRows = useMemo(() => {
        const { onRoadPrice, downPayment, activeInterestRate } = activePricing;
        if (onRoadPrice <= 0) return [];

        return getEmiScheduleTenures().map((tenureMonths) => {
            const { emi, loanAmount, totalPayable } = calculateEMI({
                price: onRoadPrice,
                downPayment,
                interestRate: activeInterestRate,
                tenureMonths,
            });
            const interest = Math.max(0, Math.round(totalPayable - loanAmount));
            return { tenureMonths, emi, interest, total: totalPayable };
        });
    }, [activePricing]);

    const leadCarLabel = [brandName, modelName, selectedVariant?.name].filter(Boolean).join(" ");

    const triggerLeadOffer = () => {
        hiddenLeadBtnRef.current?.querySelector("button")?.click();
    };

    const selectVariant = (variant: any) => {
        setSelectedVariantId(variant._id);
        setIsVariantDropdownOpen(false);
        window.dispatchEvent(new CustomEvent(EMI_SELECTED_VARIANT_EVENT, { detail: variant }));
    };

    if (!selectableVariants.length || !scheduleRows.length) {
        return null;
    }

    const tableRows = scheduleRows.map(({ tenureMonths, emi, interest, total }) => [
        <>
            <span className="text-[15px] font-bold text-[#101828]">{tenureMonths}</span>
            <span className="text-[13px] font-normal text-[#8B93A0]"> mo</span>
        </>,
        <span className="text-[14px] font-bold text-[#101828]">₹{formatEmiCurrency(emi)}</span>,
        <span className="text-[14px] font-medium text-[#6B7280]">₹{formatEmiCurrency(interest)}</span>,
        <span className="text-[14px] font-medium text-[#374151]">₹{formatEmiCurrency(total)}</span>,
    ]);

    return (
        <section
            className={`mt-6 rounded-[12px] border border-[#D8DEE8] bg-white p-4 md:p-5 ${className}`.trim()}
            data-testid="emi-schedule-widget"
        >
            <h2 className="mb-1 text-[18px] font-bold leading-6 tracking-normal text-[#101828] md:text-[20px] md:leading-[26px]">
                {`${brandName} ${modelName}`.trim()} EMI Schedule
            </h2>
            <p className="mb-4 text-[13px] leading-5 text-[#8B93A0] md:text-[14px]">
                Loan of ₹{formatEmiCurrency(activePricing.loanAmount)} @ {activePricing.activeInterestRate.toFixed(1)}%
            </p>

            <div className="mb-4">
                <Dropdown
                    trigger={
                        <Button
                            type="button"
                            variant="secondary"
                            className="h-11 w-full justify-between rounded-xl border border-[#E5E7EB] bg-white px-4 text-[14px] font-semibold text-[#101828] hover:bg-white"
                        >
                            <span className="truncate">{selectedVariant?.name || "Select variant"}</span>
                            <Icon name="downCaret" width={12} height={12} fill="#6B7280" />
                        </Button>
                    }
                    align="left"
                    sideOffset={4}
                    className="w-full font-sans"
                    matchTriggerWidth
                    open={isVariantDropdownOpen}
                    onOpenChange={setIsVariantDropdownOpen}
                >
                    <DropdownList className="max-h-64 overflow-y-auto">
                        {selectableVariants.map((variant) => (
                            <DropdownItem key={variant._id} onClick={() => selectVariant(variant)}>
                                {variant.name}
                            </DropdownItem>
                        ))}
                    </DropdownList>
                </Dropdown>
            </div>

            <div className="overflow-hidden rounded-[10px] border border-solid border-[#E8ECF0] [&>div]:!border-0 [&>div]:!rounded-none">
                <Table
                    bordered
                    headers={["Tenure", "EMI", "Interest", "Total"]}
                    rows={tableRows}
                    className="[&_th]:!border [&_th]:!border-solid [&_th]:!border-[#E8ECF0] [&_td]:!border [&_td]:!border-solid [&_td]:!border-[#E8ECF0] [&_th]:!text-[14px] [&_th]:font-semibold [&_th]:leading-5 [&_th]:text-[#8B93A0] [&_th]:bg-[#FAFAFA] [&_td]:!px-2 [&_td]:!py-2 [&_th]:!px-2 md:[&_td]:!px-3 md:[&_th]:!px-3 [&_tbody_tr]:transition-colors [&_tbody_tr:hover_td]:!bg-[#EEFFF4]"
                    tableContainerClassName="!overflow-visible"
                />
            </div>

            <p className="mt-3 text-[11px] leading-4 text-[#8B93A0] md:text-[12px]">
                Tenure in months. Rates indicative.
            </p>

            {!isDiscontinued ? (
                <>
                    <button
                        type="button"
                        onClick={triggerLeadOffer}
                        className="mt-4 flex h-12 w-full items-center justify-center rounded-[8px] border-0 bg-[#008C8B] text-[14px] font-medium leading-none text-white hover:bg-[#007A79]"
                    >
                        Check EMI Offers
                    </button>
                    <div ref={hiddenLeadBtnRef} className="sr-only" aria-hidden>
                        <LeadButton
                            carId={carId}
                            variantId={selectedVariant?._id}
                            carName={leadCarLabel}
                            btnText="Check EMI Offers"
                            is_upcoming={isUpcoming}
                            formHeading={
                                isUpcoming ? (
                                    <>
                                        Get Launch Updates on{" "}
                                        <span className="font-semibold" style={{ color: BRAND_COLOR }}>
                                            {leadCarLabel}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        Get EMI offers for{" "}
                                        <span className="font-semibold" style={{ color: BRAND_COLOR }}>
                                            {leadCarLabel}
                                        </span>
                                    </>
                                )
                            }
                            className="flex h-12 w-full items-center justify-center rounded-[8px] border-0 bg-[#008C8B] text-[14px] font-medium leading-none text-white hover:bg-[#007A79]"
                            useSplashPopup
                        />
                    </div>
                </>
            ) : null}
        </section>
    );
}
